import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getPrinters } from "../../actions/printer";
import {
  getTemplates,
  getTemplateStats,
  setTemplateStatus,
  deleteTemplate,
  getStickerQr,
} from "../../actions/template";
import {
  STATUSES,
  NO_CATEGORY,
  categoryIcon,
  money,
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
  smallButton,
} from "./templateLabels";

// Document templates customers fill in on cloudprint.mn/templates and print at a
// kiosk, with how far customers get with each one.

const PERIODS = [
  { value: "7", label: "7 хоног" },
  { value: "30", label: "30 хоног" },
  { value: "90", label: "90 хоног" },
  { value: "", label: "Бүгд" },
];

const TABS = [
  { value: "all", label: "Бүгд" },
  { value: "published", label: STATUSES.published.label },
  { value: "draft", label: STATUSES.draft.label },
  { value: "archived", label: STATUSES.archived.label },
];

const categoryOf = (item) => item.category || NO_CATEGORY;
// What the search looks in
const searchTextOf = (item) =>
  [item.name, item.slug, item.category, item.description].join(" ").toLowerCase();

// The status, category and search stay for the tab's session, so they are still there
// after editing a template. Session storage may be unavailable (private mode).
const FILTER_KEY = "templates-filter";
const savedFilter = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(FILTER_KEY)) || {};
  } catch (err) {
    return {};
  }
};
const saveFilter = (filter) => {
  try {
    window.sessionStorage.setItem(FILTER_KEY, JSON.stringify(filter));
  } catch (err) {}
};

// How far customers get with a form, in order
const STEPS = [
  {
    key: "shown",
    label: "Харсан",
    hint: "Киоскийн дэлгэц загварын QR-ыг харуулсан",
    icon: "fa-tv",
    tone: "text-gray-500 bg-gray-100 dark:text-gray-100 dark:bg-gray-500",
    text: "text-gray-500",
    bar: "bg-gray-400",
  },
  {
    key: "opened",
    label: "Нээсэн",
    hint: "Утсаараа маягт нээсэн",
    icon: "fa-eye",
    tone: "text-blue-500 bg-blue-100 dark:text-blue-100 dark:bg-blue-500",
    text: "text-blue-500",
    bar: "bg-blue-500",
  },
  {
    key: "generated",
    label: "QR авсан",
    hint: "Бөглөөд PDF үүсгэсэн",
    icon: "fa-qrcode",
    tone: "text-purple-500 bg-purple-100 dark:text-purple-100 dark:bg-purple-500",
    text: "text-purple-500",
    bar: "bg-purple-500",
  },
  {
    key: "scanned",
    label: "Уншуулсан",
    hint: "Киоск QR-ыг уншсан, PDF тутамд нэг удаа",
    icon: "fa-print",
    tone: "text-green-500 bg-green-100 dark:text-green-100 dark:bg-green-500",
    text: "text-green-500",
    bar: "bg-green-500",
  },
  {
    key: "paid",
    label: "Төлсөн",
    hint: "Төлбөр орсон invoice, invoice тутамд нэг удаа",
    icon: "fa-credit-card",
    tone: "text-pink-500 bg-pink-100 dark:text-pink-100 dark:bg-pink-500",
    text: "text-pink-500",
    bar: "bg-pink-500",
  },
];

// Revenue is the sum of paid invoices, shown in ₮
const COUNTS = [...STEPS.map((step) => step.key), "revenue"];
const EMPTY = COUNTS.reduce((total, key) => ({ ...total, [key]: 0 }), {});

// Rows saved before a counter existed have none
const add = (total, row) =>
  COUNTS.reduce((sum, key) => ({ ...sum, [key]: total[key] + (row[key] || 0) }), {});

const count = (value) => Number(value || 0).toLocaleString();

const card = "bg-white rounded-lg shadow-sm dark:bg-gray-800";
const iconButton = smallButton + " inline-flex items-center";
const dangerButton =
  "inline-flex items-center px-3 py-1 text-xs font-medium leading-5 text-red-600 transition-colors duration-150 bg-white border border-red-200 rounded-lg dark:text-red-400 dark:bg-gray-700 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 focus:outline-none";

const StatCard = ({ icon, tone, label, value, hint }) => (
  <div className={"flex items-center p-4 " + card}>
    <div
      className={
        "flex items-center justify-center flex-shrink-0 w-12 h-12 mr-4 rounded-full " +
        tone
      }
    >
      <i className={"fas " + icon} />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
      <p className="text-2xl font-semibold text-gray-700 dark:text-gray-200">{value}</p>
      <p className="text-xs text-gray-500 truncate" title={hint}>
        {hint}
      </p>
    </div>
  </div>
);

// One bar per step, as long as its share of the largest step
const Funnel = ({ row }) => {
  const top = Math.max(...STEPS.map((step) => row[step.key]), 1);
  return (
    <div className="space-y-2">
      {STEPS.map((step) => {
        const value = row[step.key];
        const width = value > 0 ? Math.max((value / top) * 100, 3) : 0;
        return (
          <div key={step.key} className="flex items-center gap-3 text-xs" title={step.hint}>
            <span className="flex-shrink-0 w-20 text-gray-500 dark:text-gray-400">
              {step.label}
            </span>
            <div className="flex-1 h-2 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-700">
              <div
                className={"h-full rounded-full transition-all duration-500 " + step.bar}
                style={{ width: width + "%" }}
              />
            </div>
            <span className="flex-shrink-0 w-10 font-semibold text-right text-gray-700 dark:text-gray-200">
              {count(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Kiosk name on its own line so the counts fit a narrow card
const KioskList = ({ rows, kioskName }) => (
  <div className="mt-3 overflow-hidden text-xs rounded-lg bg-gray-50 dark:bg-gray-900">
    <div className="grid grid-cols-7 gap-1 px-3 py-2 font-medium text-gray-500 dark:text-gray-400">
      {STEPS.map((step) => (
        <span key={step.key} className="text-right" title={step.label}>
          <i className={"fas " + step.icon + " " + step.text} />
        </span>
      ))}
      <span className="col-span-2 text-right">Орлого</span>
    </div>
    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
      {rows.map((row) => (
        <li key={row.printer || "none"} className="px-3 py-2 text-gray-700 dark:text-gray-300">
          <p className="truncate" title={kioskName(row.printer)}>
            {kioskName(row.printer)}
          </p>
          <div className="grid grid-cols-7 gap-1 mt-1">
            {STEPS.map((step) => (
              <span key={step.key} className="text-right">
                {count(row[step.key])}
              </span>
            ))}
            <span className="col-span-2 font-semibold text-right">
              {money(row.revenue || 0)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const Templates = ({
  getTemplates,
  getTemplateStats,
  setTemplateStatus,
  deleteTemplate,
  getStickerQr,
  getPrinters,
  template: { templates, stats, loading },
  printers,
}) => {
  const [days, setDays] = useState("30");
  const [tab, setTab] = useState(() => savedFilter().tab || "all");
  const [category, setCategory] = useState(() => savedFilter().category || "all");
  const [query, setQuery] = useState(() => savedFilter().query || "");
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [stickerPrinter, setStickerPrinter] = useState("");
  const [sticker, setSticker] = useState(null);

  useEffect(() => {
    getTemplates();
    // Kiosk names for the per-kiosk rows; the Printer page may have loaded them
    if (printers.length === 0) getPrinters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getTemplateStats(days);
  }, [days, getTemplateStats]);

  useEffect(() => {
    saveFilter({ tab, category, query });
  }, [tab, category, query]);

  const kioskName = (id) => {
    if (!id) return "Киоскгүй (cloudprint.mn-ээс шууд)";
    const printer = printers.find((item) => item._id === id);
    return printer
      ? printer.printerName + (printer.location ? " - " + printer.location : "")
      : id;
  };

  const rowsOf = (templateId) => stats.filter((row) => row.template === templateId);

  const changeStatus = async (item, status) => {
    setBusyId(item._id);
    await setTemplateStatus(item._id, status);
    setBusyId(null);
  };

  const remove = async (item) => {
    if (!window.confirm(`"${item.name}" загварыг устгах уу?`)) return;
    setBusyId(item._id);
    await deleteTemplate(item._id);
    setBusyId(null);
  };

  const makeSticker = async () => {
    setSticker(await getStickerQr(stickerPrinter));
  };

  const actions = (item) => {
    const busy = busyId === item._id;
    const button = (label, icon, onClick, className = iconButton) => (
      <button
        key={label}
        className={className + (busy ? " opacity-50 cursor-wait" : "")}
        disabled={busy}
        onClick={onClick}
      >
        <i className={"mr-1.5 fas " + icon} />
        {label}
      </button>
    );
    const buttons = [];
    if (item.status === "draft") {
      buttons.push(button("Устгах", "fa-trash-alt", () => remove(item), dangerButton));
      buttons.push(button("Нийтлэх", "fa-globe", () => changeStatus(item, "published")));
    }
    if (item.status === "published") {
      buttons.push(button("Архивлах", "fa-archive", () => changeStatus(item, "archived")));
      buttons.push(button("Нуух", "fa-eye-slash", () => changeStatus(item, "draft")));
    }
    if (item.status === "archived") {
      buttons.push(button("Сэргээх", "fa-undo", () => changeStatus(item, "draft")));
    }
    buttons.push(
      <Link
        key="edit"
        to={"/template-edit?id=" + item._id}
        className="inline-flex items-center px-3 py-1 text-xs font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 rounded-lg hover:bg-purple-700"
      >
        <i className="mr-1.5 fas fa-pen" />
        Засах
      </Link>
    );
    return buttons;
  };

  const templateCard = (item) => {
    const kioskRows = rowsOf(item._id);
    const total = kioskRows.reduce(add, EMPTY);
    const status = STATUSES[item.status] || STATUSES.draft;
    const open = expanded === item._id;
    return (
      <div
        key={item._id}
        className={
          "flex flex-col p-4 " + card + (item.status === "archived" ? " opacity-75" : "")
        }
      >
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-purple-600 bg-purple-100 rounded-lg dark:text-purple-300 dark:bg-purple-900">
            <i className={"fas " + categoryIcon(item.category)} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-semibold text-gray-700 truncate dark:text-gray-200"
              title={item.name}
            >
              {item.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {item.slug}
              {item.category ? " · " + item.category : ""} · v{item.version}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <span
              className={
                "inline-block px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
                status.badge
              }
            >
              {status.label}
            </span>
            {item.price > 0 && (
              <p
                className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-200"
                title="Киоскийн нэг хувийн үнэ"
              >
                {money(item.price)}/хувь
              </p>
            )}
          </div>
        </div>

        {item.description && (
          <p
            className="mt-3 text-sm text-gray-600 truncate dark:text-gray-400"
            title={item.description}
          >
            {item.description}
          </p>
        )}

        <div className="mt-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Явц
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Орлого
              <span className="ml-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                {money(total.revenue)}
              </span>
            </span>
          </div>
          <Funnel row={total} />
          {kioskRows.length > 0 && (
            <button
              className="inline-flex items-center mt-3 text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline focus:outline-none"
              onClick={() => setExpanded(open ? null : item._id)}
            >
              <i className="mr-1.5 fas fa-desktop" />
              {open ? "Хураах" : `Киоскоор (${kioskRows.length})`}
              <i className={"ml-1.5 fas fa-chevron-" + (open ? "up" : "down")} />
            </button>
          )}
          {open && <KioskList rows={kioskRows} kioskName={kioskName} />}
        </div>

        <div className="pt-4 mt-auto">
          <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
            {actions(item)}
          </div>
        </div>
      </div>
    );
  };

  const overall = stats.reduce(add, EMPTY);

  // Every word must be found. Each filter counts what the other two leave, so its
  // count is what clicking it shows.
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const found = templates.filter((item) => words.every((word) => searchTextOf(item).includes(word)));
  // In the order of their first template, as the list comes sorted
  const categories = [...new Set(templates.map(categoryOf))];
  const activeCategory = categories.includes(category) ? category : "all";
  const inStatus = (item, value) => value === "all" || item.status === value;
  const inCategory = (item, value) => value === "all" || categoryOf(item) === value;
  const shown = found.filter((item) => inStatus(item, tab) && inCategory(item, activeCategory));
  const countOf = (value) =>
    found.filter((item) => inStatus(item, value) && inCategory(item, activeCategory)).length;
  const categoryCount = (value) =>
    found.filter((item) => inStatus(item, tab) && inCategory(item, value)).length;
  // With every category shown, each one is a group under its own heading
  const groups =
    activeCategory === "all"
      ? categories
          .map((name) => ({ name, items: shown.filter((item) => categoryOf(item) === name) }))
          .filter((group) => group.items.length > 0)
      : [{ name: activeCategory, items: shown }];
  const filtered = words.length > 0 || activeCategory !== "all" || tab !== "all";
  const clearFilter = () => {
    setTab("all");
    setCategory("all");
    setQuery("");
  };

  const categoryChip = ({ value, label, icon }) => {
    const active = activeCategory === value;
    const total = categoryCount(value);
    return (
      <button
        key={value}
        type="button"
        aria-pressed={active}
        onClick={() => setCategory(value)}
        className={
          "inline-flex items-center px-3 py-1 text-sm font-medium border rounded-full transition-colors duration-150 focus:outline-none " +
          (active
            ? "text-white bg-purple-600 border-purple-600"
            : "text-gray-600 bg-white border-gray-200 hover:border-purple-300 hover:text-purple-700 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:text-purple-300") +
          (total === 0 && !active ? " opacity-50" : "")
        }
      >
        <i className={"mr-1.5 fas " + icon} />
        {label}
        <span className={"ml-1.5 text-xs " + (active ? "text-purple-100" : "text-gray-400")}>
          {total}
        </span>
      </button>
    );
  };

  return (
    <Admin>
      <>
        <div className="flex flex-wrap items-end justify-between gap-4 my-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">
              Загварууд
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Хэрэглэгчид cloudprint.mn/templates дээр бөглөөд киоскоор хэвлэдэг маягтууд
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/template-themes"
              className={secondaryButton + " inline-flex items-center whitespace-nowrap"}
            >
              <i className="mr-2 fas fa-palette" />
              Дизайнууд
            </Link>
            <Link
              to="/template-edit"
              className={primaryButton + " inline-flex items-center whitespace-nowrap"}
            >
              <i className="mr-2 fas fa-plus" />
              Шинэ загвар
            </Link>
          </div>
        </div>

        {loading ? (
          <img
            src={spinner}
            className="my-6"
            style={{ width: "50px", margin: "auto", display: "block" }}
            alt="Loading..."
          />
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-12 mb-8 text-center bg-white border-2 border-gray-200 border-dashed rounded-lg dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center justify-center w-12 h-12 mb-4 text-purple-600 bg-purple-100 rounded-full dark:text-purple-300 dark:bg-purple-900">
              <i className="fas fa-file-alt" />
            </div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Загвар алга</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Эхний загвараа үүсгээд нийтэлбэл хэрэглэгчид cloudprint.mn/templates дээр харна.
            </p>
            <Link
              to="/template-edit"
              className={primaryButton + " inline-flex items-center mt-4"}
            >
              <i className="mr-2 fas fa-plus" />
              Шинэ загвар
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                Хэрэглэгчдийн явц
              </h3>
              <div className={"inline-flex p-1 " + card} role="group" aria-label="Хугацаа">
                {PERIODS.map((period) => {
                  const active = days === period.value;
                  return (
                    <button
                      key={period.value}
                      aria-pressed={active}
                      onClick={() => setDays(period.value)}
                      className={
                        "px-3 py-1 text-sm font-medium rounded-md transition-colors duration-150 focus:outline-none " +
                        (active
                          ? "text-white bg-purple-600"
                          : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200")
                      }
                    >
                      {period.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 xl:grid-cols-3">
              {STEPS.map((step) => (
                <StatCard
                  key={step.key}
                  icon={step.icon}
                  tone={step.tone}
                  label={step.label}
                  value={count(overall[step.key])}
                  hint={step.hint}
                />
              ))}
              <StatCard
                icon="fa-coins"
                tone="text-yellow-500 bg-yellow-100 dark:text-yellow-100 dark:bg-yellow-500"
                label="Орлого"
                value={money(overall.revenue)}
                hint="Төлсөн invoice-уудын дүн"
              />
            </div>

            <div className="flex gap-4 mb-4 overflow-x-auto border-b sm:gap-6 border-gray-200 dark:border-gray-700">
              {TABS.map((item) => {
                const active = tab === item.value;
                return (
                  <button
                    key={item.value}
                    onClick={() => setTab(item.value)}
                    className={
                      "flex items-center pb-3 text-sm font-medium border-b-2 whitespace-nowrap focus:outline-none " +
                      (active
                        ? "text-purple-600 border-purple-600 dark:text-purple-400 dark:border-purple-400"
                        : "text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200")
                    }
                  >
                    {item.label}
                    <span
                      className={
                        "ml-2 px-2 py-0.5 text-xs rounded-full " +
                        (active
                          ? "text-purple-700 bg-purple-100 dark:text-purple-200 dark:bg-purple-900"
                          : "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-700")
                      }
                    >
                      {countOf(item.value)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-3 mb-6">
              <div className="relative w-full sm:max-w-md">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Хайх: нэр, slug, тайлбар"
                  aria-label="Загвар хайх"
                  className={fieldClass + " pl-9"}
                />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Ангилал">
                {[
                  { value: "all", label: "Бүх ангилал", icon: "fa-th-large" },
                  ...categories.map((name) => ({ value: name, label: name, icon: categoryIcon(name) })),
                ].map(categoryChip)}
              </div>
            </div>

            {shown.length === 0 ? (
              <div className="py-8 text-sm text-center text-gray-500 dark:text-gray-400">
                <p>{filtered ? "Шүүлтүүрт тохирох загвар алга." : "Загвар алга."}</p>
                {filtered && (
                  <button type="button" className={iconButton + " mt-3"} onClick={clearFilter}>
                    <i className="mr-1.5 fas fa-times" />
                    Шүүлтүүрийг цэвэрлэх
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {groups.map((group) => (
                  <section key={group.name}>
                    {groups.length > 1 && (
                      <h3 className="flex items-center mb-3 text-sm font-semibold text-gray-600 dark:text-gray-300">
                        <i className={"mr-2 fas " + categoryIcon(group.name)} />
                        {group.name}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {group.items.length}
                        </span>
                      </h3>
                    )}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {group.items.map(templateCard)}
                    </div>
                  </section>
                ))}
              </div>
            )}

            <p className="flex items-start mt-4 mb-8 text-xs text-gray-500 dark:text-gray-400">
              <i className="mt-0.5 mr-2 fas fa-info-circle" />
              Орлогоос хэвлэлт тасарсны буцаалтыг хасаагүй. Харсан, Төлсөн нь киоск
              1.0.13-аас эхэлж тоологдоно.
            </p>
          </>
        )}

        <div className={"mb-8 overflow-hidden " + card}>
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="p-6 md:col-span-2">
              <div className="flex items-center mb-2">
                <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-3 text-purple-600 bg-purple-100 rounded-lg dark:text-purple-300 dark:bg-purple-900">
                  <i className="fas fa-qrcode" />
                </div>
                <h3 className="font-semibold text-gray-700 dark:text-gray-200">Наалтын QR</h3>
              </div>
              <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                Киоскийн хажууд наах QR. Утсаараа уншуулахад cloudprint.mn/templates
                нээгдэнэ. Киоск сонговол тэр QR-аас орсон хүмүүсийг тухайн киоскоор
                тоолно.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex-1 block min-w-0 sm:max-w-xs">
                  <span className={labelClass}>Киоск</span>
                  <select
                    value={stickerPrinter}
                    onChange={(e) => {
                      setStickerPrinter(e.target.value);
                      setSticker(null);
                    }}
                    className={fieldClass + " pr-10"}
                  >
                    <option value="">Киоскгүй (ерөнхий)</option>
                    {printers.map((printer) => (
                      <option key={printer._id} value={printer._id}>
                        {printer.printerName}
                        {printer.location ? " - " + printer.location : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className={primaryButton + " inline-flex items-center"}
                  onClick={makeSticker}
                >
                  <i className="mr-2 fas fa-magic" />
                  QR үүсгэх
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900 md:border-l border-gray-100 dark:border-gray-700">
              {sticker ? (
                <>
                  <img
                    src={sticker.qr}
                    alt="Наалтын QR"
                    className="w-40 h-40 p-2 bg-white rounded-lg shadow-sm"
                  />
                  <p className="max-w-xs mt-3 text-xs text-center text-gray-500 break-all dark:text-gray-400">
                    {sticker.url}
                  </p>
                  <a
                    href={sticker.qr}
                    download={
                      "template-qr" +
                      (stickerPrinter
                        ? "-" +
                          (printers.find((item) => item._id === stickerPrinter) || {})
                            .printerName
                        : "") +
                      ".png"
                    }
                    className={iconButton + " mt-3"}
                  >
                    <i className="mr-1.5 fas fa-download" />
                    PNG татах
                  </a>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center w-40 h-40 text-gray-400 border-2 border-gray-200 border-dashed rounded-lg dark:border-gray-700 dark:text-gray-500">
                  <i className="text-3xl fas fa-qrcode" />
                  <span className="mt-2 text-xs">QR энд гарна</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    </Admin>
  );
};

Templates.propTypes = {
  getTemplates: PropTypes.func.isRequired,
  getTemplateStats: PropTypes.func.isRequired,
  template: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  template: state.template,
  printers: state.printer.printers,
});

export default connect(mapStateToProps, {
  getTemplates,
  getTemplateStats,
  setTemplateStatus,
  deleteTemplate,
  getStickerQr,
  getPrinters,
})(Templates);
